import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { klona } from 'klona';
import { Divider } from 'antd';
import service from '@/service';
import { FiledNameByBodyPart, MeasurementValueByBodyPart } from './enumData';
import BodyMeasurementComponent from './body-measurement-component';
import CustomLabel from './custom-Label';

import cx from './index.module.scss';

export interface MeasurementContentRefAttrs {
  getData: (orderId: string) => void;
}

interface MeasurementContentProps {
  afterMassage?: boolean;
  currentServingOrder: GetServiceOrderListResponse;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  setMeasurementResult: React.Dispatch<React.SetStateAction<MeasurementResult>>;
  handleScrollElement?: VoidFunc;
}

const MeasurementContent = forwardRef<MeasurementContentRefAttrs, MeasurementContentProps>(
  ({ afterMassage = false, setMeasurementResult, currentServingOrder, setLoading, handleScrollElement }, ref) => {
    const [lastMeasurementResult, setLastMeasurementResult] = useState<GetLastMeasurementResponse>(null);
    const [beforeHandData, setBeforeHandData] = useState<MeasurementResult>(null);

    // 近期围度测量数组
    const LastGirthMeasurementList = useMemo(() => {
      const defaultFiledName = FiledNameByBodyPart?.[currentServingOrder?.bodyPart];
      const formatArr = defaultFiledName?.map(item => {
        return {
          ...item,
          value: parseFloat((lastMeasurementResult?.[item.name] / 10).toFixed(1)),
        };
      });
      return formatArr;
    }, [currentServingOrder?.bodyPart, lastMeasurementResult]);

    const BodyMeasuresItems = useMemo(() => {
      return MeasurementValueByBodyPart?.[currentServingOrder?.bodyPart];
    }, [currentServingOrder?.bodyPart]);

    const isHasExtraPadding = useMemo(() => {
      if (afterMassage) {
        return currentServingOrder?.bodyPart === 'LowerBody' || currentServingOrder?.onlyManual;
      }
      return false;
    }, [afterMassage, currentServingOrder?.bodyPart, currentServingOrder?.onlyManual]);

    const getLastMeasurementResult = useCallback(
      async orderId => {
        try {
          const { data } = await service.bodyMeasurementApi.getLastMeasurement(orderId);
          setLastMeasurementResult(data);
        } finally {
          setLoading(false);
        }
      },
      [setLoading]
    );

    const getMeasurementBeforeData = useCallback(
      async orderId => {
        try {
          const { data } = await service.bodyMeasurementApi.getMeasurementResult(orderId);

          const newData = klona(data?.before);
          for (let key in newData) {
            if (newData?.hasOwnProperty(key)) {
              // eslint-disable-next-line max-depth
              if (typeof newData?.[key] === 'number') {
                newData[key] = parseFloat((newData?.[key] / 10).toFixed(1));
              }
            }
          }

          setBeforeHandData(newData);
          if (!afterMassage) {
            setMeasurementResult(data?.before);
          }
        } catch (e) {}
      },
      [afterMassage, setMeasurementResult]
    );

    useImperativeHandle(
      ref,
      () => ({
        getData: (orderId: string) => {
          getMeasurementBeforeData(orderId);
          getLastMeasurementResult(orderId);
        },
      }),
      [getLastMeasurementResult, getMeasurementBeforeData]
    );

    return (
      <div className={cx('measurement', { 'extra-padding': isHasExtraPadding })}>
        {!afterMassage &&
          LastGirthMeasurementList !== null &&
          LastGirthMeasurementList?.some(item => lastMeasurementResult?.[item?.name] !== null) && (
            <CustomLabel label="近期围度测量" desc="请持续记录见证您的顾客的变化吧" classNames={cx('last-measurement')}>
              <div className={cx('measurement-data')}>
                {LastGirthMeasurementList?.filter(item => item.value)?.map((item, index) => (
                  <div key={item.name} className={cx('measurement-data-item')}>
                    <Divider type="vertical" className={cx('line', { hide: index === 0 })} />
                    <div className={cx('content')}>
                      <span className={cx('label')}>
                        {currentServingOrder?.onlyManual || currentServingOrder?.bodyPart === 'LowerBody'
                          ? item.label + '围'
                          : item.label}
                      </span>
                      <span className={cx('value')}>{item.value || '-'}</span>
                      <span className={cx('label', { hide: item.value === null })}>{item.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CustomLabel>
          )}

        <CustomLabel
          label={afterMassage ? '理疗后围度复量' : '理疗前围度测量'}
          desc="请为您的顾客测量以下围度，并通过平板拍照上传"
        >
          <BodyMeasurementComponent
            afterMassage={afterMassage}
            lastGirthMeasurementList={LastGirthMeasurementList}
            beforeHandData={beforeHandData}
            onResultChange={setMeasurementResult}
            bodyMeasuresItems={BodyMeasuresItems}
            handleScrollElement={handleScrollElement}
          />
        </CustomLabel>
      </div>
    );
  }
);

export default MeasurementContent;
